/**
 * Cible d'un bouton — résolution unique, partagée par la box Bouton, le CTA
 * d'une slide de carrousel et la page de connexion.
 *
 * Une prop objet `link` remplace le champ URL libre :
 *
 *   { kind: 'page' | 'block' | 'external' | 'email' | 'phone' | 'variable' | 'bank' | 'download',
 *     pageSlug, blockId, anchor, url, email, phone, newTab,
 *     variableKey, variableName, variableValue,
 *     linkId, linkLabel, filename, documentId, form }
 *
 * L'ancienne prop `url` reste lue — il y a du web_data réel en production — et
 * garde exactement son comportement d'avant : ouverture dans un nouvel onglet.
 */

/** Types de cible, dans l'ordre du sélecteur. */
export const BUTTON_LINK_KINDS = ['page', 'block', 'external', 'email', 'phone', 'variable', 'bank', 'download'];

export const DEFAULT_BUTTON_LINK = {
  kind: 'external',
  pageSlug: '',
  blockId: '',
  anchor: '',
  url: '',
  email: '',
  phone: '',
  newTab: true,
  // Cible « variable » : la clé sert au rafraîchissement, le nom à l'affichage
  // quand la clé a disparu du CRM, la valeur au rendu. Même triplet que le
  // nœud TipTap d'une variable dans un texte (chantier n°1).
  variableKey: '',
  variableName: '',
  variableValue: '',
  /* Cible « banque de liens » : `linkId` sert au rafraîchissement, `linkLabel`
     à afficher un nom lisible quand le lien a été supprimé de la banque, et
     l'adresse elle-même est copiée dans `url` — la même que le type « adresse
     web ». Le site publié n'interroge donc jamais la banque, et supprimer un
     lien ne casse aucun site (décision du chantier n°16). */
  linkId: '',
  linkLabel: '',
};

/**
 * La chaîne ressemble-t-elle à une adresse web ?
 *
 * Sert deux fois : à ne proposer que les variables plausibles dans le
 * sélecteur, et à refuser de poser un `href` sur une valeur qui n'en est pas
 * une. Aucune variable n'est typée côté back — le catalogue n'est qu'un sac de
 * chaînes — donc c'est le seul filtre dont on dispose.
 *
 * Volontairement plus strict que `safeUrl`, qui préfixe « https:// » à
 * n'importe quoi : « Agence Nord » y deviendrait `https://Agence Nord`, un lien
 * vivant qui mène à une erreur DNS. On préfère un bouton inerte.
 */
export function looksLikeUrl(raw) {
  const value = String(raw || '').trim();
  if (!value || /\s/.test(value)) return false;
  if (/^https?:\/\//i.test(value)) return value.length > 'https://'.length;
  // Domaine nu : « acme.fr », « acme.fr/devis ». Un TLD d'au moins deux lettres.
  return /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(\/|\?|#|$)/i.test(value);
}

/**
 * Schémas d'URL acceptés. Tout le reste — `javascript:` en tête — est refusé :
 * l'URL vient de l'utilisateur et finit dans un `href`.
 */
const SAFE_SCHEME = /^(https?:|mailto:|tel:|\/|#)/i;

function safeUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  // Une saisie sans schéma (« exemple.com ») est un lien externe : le préfixer
  // évite un href relatif qui pointerait dans le site.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(value) && !/^[/#]/.test(value)) {
    return `https://${value}`;
  }
  return SAFE_SCHEME.test(value) ? value : '';
}

/**
 * Props d'un bouton → attributs du lien. Fonction pure, seul point testé de la
 * navigation.
 *
 * @param {Object|undefined} link objet `link`, absent sur les boutons anciens
 * @param {string|undefined} legacyUrl ancienne prop `url`
 * @returns {{href?: string, target?: string, rel?: string}}
 */
export function resolveButtonLink(link, legacyUrl) {
  const kind = link?.kind;
  if (!kind) return external(safeUrl(legacyUrl), true);

  const l = { ...DEFAULT_BUTTON_LINK, ...link };
  switch (kind) {
    // Une cible interne n'ouvre JAMAIS un nouvel onglet : le lecteur y perdrait
    // le site qu'il est en train de parcourir.
    case 'page':
      return l.pageSlug ? { href: `#${l.pageSlug}` } : {};
    /* Toujours l'id du bloc, jamais son ancre : l'id ne change jamais, l'ancre
       se renomme. Un lien qui pointait `#nos-tarifs` mourait en silence dès que
       le bloc visé était rebaptisé, alors qu'il existait toujours. L'ancre
       reste utile — le viewer la résout, on peut l'écrire à la main — mais un
       bouton ne s'appuie plus dessus. Repli sur l'ancre pour les liens écrits
       avant cette règle, qui n'ont peut-être que ça. */
    case 'block': {
      const target = l.blockId || l.anchor;
      return target ? { href: `#${target}` } : {};
    }
    case 'download': {
      const href = safeUrl(l.url);
      return /^https?:\/\//i.test(href) || /^\/(?!\/)/.test(href)
        ? { href, download: l.filename || '' } : {};
    }
    case 'email':
      return l.email ? { href: `mailto:${l.email.trim()}` } : {};
    case 'phone':
      // Les espaces d'un numéro écrit « 01 23 45 67 89 » cassent le tel:.
      return l.phone ? { href: `tel:${l.phone.replace(/\s+/g, '')}` } : {};
    // Un lien de la banque porte l'adresse copiée dans `url` : une fois posé,
    // il se comporte exactement comme une adresse saisie à la main.
    case 'bank':
    case 'external':
      return external(safeUrl(l.url), l.newTab !== false);
    /* Une valeur vide ou qui n'est pas une adresse laisse le bouton **inerte**
       plutôt que de le faire disparaître : la mise en page ne doit pas bouger
       d'un document à l'autre selon ce que le client a rempli dans le
       formulaire. */
    case 'variable':
      return looksLikeUrl(l.variableValue)
        ? external(safeUrl(l.variableValue), l.newTab !== false)
        : {};
    default:
      return {};
  }
}

function external(href, newTab) {
  if (!href) return {};
  // `rel` n'accompagne que l'ouverture dans un nouvel onglet — c'est là qu'il
  // sert (window.opener), pas ailleurs.
  return newTab
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : { href };
}

/**
 * L'objet `link` d'un bouton qui n'en a pas encore : sa vieille URL, pour que
 * le sélecteur s'ouvre sur ce que l'utilisateur avait saisi.
 */
export function buttonLinkFromLegacy(url) {
  return { ...DEFAULT_BUTTON_LINK, kind: 'external', url: String(url || '') };
}
